export interface IncomingCallRingtoneAudio {
  loop: boolean
  preload: string
  currentTime: number
  play: () => Promise<void> | void
  pause: () => void
}

interface IncomingCallRingtoneOptions {
  createAudio?: (url: string) => IncomingCallRingtoneAudio
  logger?: Pick<Console, 'warn'>
  url?: string
}

function publicAssetUrl(path: string) {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')
  return `${base}${path.replace(/^\//, '')}`
}

export const INCOMING_CALL_RINGTONE_URL = publicAssetUrl('audio/video_call_incoming.mp3')

export function createIncomingCallRingtonePlayer(options: IncomingCallRingtoneOptions = {}) {
  const createAudio = options.createAudio || ((url: string) => new Audio(url))
  const logger = options.logger || console
  const ringtoneUrl = options.url || INCOMING_CALL_RINGTONE_URL
  let activeOrderID: string | null = null
  let audio: IncomingCallRingtoneAudio | null = null

  const stopAudio = (target: IncomingCallRingtoneAudio | null) => {
    if (!target) {
      return
    }
    try {
      target.pause()
    } catch {
      // Ignore browser audio cleanup failures.
    }
    try {
      target.currentTime = 0
    } catch {
      // Some browser audio objects may reject currentTime before metadata loads.
    }
  }

  return {
    start(orderID: string | null | undefined) {
      if (!orderID) {
        this.stop()
        return
      }
      if (activeOrderID === orderID && audio) {
        return
      }
      this.stop()
      const nextAudio = createAudio(ringtoneUrl)
      nextAudio.loop = true
      nextAudio.preload = 'auto'
      activeOrderID = orderID
      audio = nextAudio
      const playResult = nextAudio.play()
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch((error) => {
          logger.warn('Incoming call ringtone playback was blocked.', error)
        })
      }
    },
    stop(orderID?: string | null) {
      if (orderID && activeOrderID !== orderID) {
        return
      }
      stopAudio(audio)
      audio = null
      activeOrderID = null
    },
    destroy() {
      this.stop()
    },
    getActiveOrderID() {
      return activeOrderID
    },
  }
}
